import { IPrompt, IQuestion, QuestionCategories } from "@sp-crm/core";
import { PrintableForm } from "components/printable-form/printable-form";
import { renderQuestionSection } from "components/printable-form/render-question-section";
import { AdvancedSearchEntityType } from "generated/graphql";
import * as React from "react";
import { useBridgeQuestionsForCurrentRegion } from "store/selectors/bridge";
import { useCommunityTypeQuestion, useRegionId, useRegions } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";

interface PricingRow {
    careType: string;
    floorPlan?: string;
    monthlyPrice?: string; // leave undefined for blank
}

interface PricingTableSectionProps {
    title: string;
    rows: PricingRow[];
}

function filterRoomTypeQuestions(value: string) {
    if (!value) {
        return false;
    }
    return value.includes("Room") || value.includes("Floor");
}

export const PricingTableSection: React.FC<PricingTableSectionProps> = ({
    title,
    rows,
}) => {
    return (
        <table
            className="w-full table-fixed border border-black text-sm"
            style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
            <thead>
                <tr className="bg-blue-100 text-center font-semibold">
                    <th className="p-2 border-b border-r border-black" colSpan={3}>
                        {title}
                    </th>
                </tr>
                <tr className="border-b border-black font-semibold">
                    <th className="p-2 border-r border-black text-left">Care Type</th>
                    <th className="p-2 border-r border-black text-left">Floor Plan</th>
                    <th className="p-2 text-left">Monthly Price</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={i} className="border-b border-black last:border-b-0">
                        <td className="p-2 border-r border-black">{row.careType}</td>
                        <td className="p-2 border-r border-black">{row.floorPlan}</td>
                        <td className="p-2"></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export const PrintableIntakeCommunity = () => {
    const questions = useBridgeQuestionsForCurrentRegion(
        AdvancedSearchEntityType.Community,
    );

    React.useEffect(() => {
        handleEvent("print-community-form");
    }, []);

    const regionId = useRegionId();
    const regions = useRegions();
    const region = regions.find(r => r.id === regionId);
    const regionKey = region?.key;
    const communityTypeQuestion = useCommunityTypeQuestion(regionKey, regionId);
    const communityTypes = communityTypeQuestion?.prompts ?? [];

    const pricingQuestions = questions
        // Get finance questions
        .filter(question => question.category === QuestionCategories.finance)
        // ...that depend on CommunityType, or
        .filter(question => {
            return (
                question.rules?.asJson?.question === communityTypeQuestion?.key ||
                filterRoomTypeQuestions(question.rules?.asJson?.question)
            );
        });

    const communityInfoSection = {
        title: "Community Information",
        fields: [
            { label: "Community Name", colSpan: 12 },
            { label: "Community Address", colSpan: 12 },
            { label: "Community City", colSpan: 4 },
            { label: "Community State", colSpan: 2 },
            { label: "Community Zip", colSpan: 2 },
            { label: "Community County", colSpan: 4 },
            { label: "Main Phone", colSpan: 4 },
            { label: "Fax number", colSpan: 4 },
            { label: "Community website", colSpan: 4 },
            { label: "Community email", colSpan: 12 },
            { label: "Virtual Tour Link", colSpan: 12 },
        ],
    };

    const contactSection = {
        title: "Contact Person",
        fields: [
            { label: "Contact Name", colSpan: 6 },
            { label: "Contact Role", colSpan: 6 },
            { label: "Contact Cell Phone", colSpan: 3 },
            { label: "Contact Other Phone", colSpan: 3 },
            { label: "Contact Email", colSpan: 6 },
            { label: "Contact Address", colSpan: 12 },
            { label: "Contact City", colSpan: 6 },
            { label: "Contact State", colSpan: 3 },
            { label: "Contact Zip", colSpan: 3 },
        ],
    };

    const communityAttributesSection = renderQuestionSection(
        "Community Attributes",
        QuestionCategories.type,
        questions,
    );

    const pricingRows = buildPricingRows(pricingQuestions, questions, communityTypes);

    communityAttributesSection.children = [
        <PricingTableSection key="pricing" title="Pricing" rows={pricingRows ?? []} />,
    ];

    const communityRecordsSection = renderQuestionSection(
        "Community Records",
        QuestionCategories.communityRecords,
        questions,
    );

    const healthSection = renderQuestionSection(
        "Health",
        QuestionCategories.physician,
        questions,
    );

    const filteredFinancesQuestions = questions.filter(question => {
        return !pricingQuestions.some(p => p.key === question.key);
    });

    const financesSection = renderQuestionSection(
        "Finances",
        QuestionCategories.finance,
        filteredFinancesQuestions,
    );

    const activitySection = renderQuestionSection(
        "Activities of Daily Living",
        QuestionCategories.activity,
        questions,
    );

    const servicesSection = renderQuestionSection(
        "Services",
        QuestionCategories.services,
        questions,
    );

    const insuranceSection = renderQuestionSection(
        "Insurance",
        QuestionCategories.insurance,
        questions,
    );

    const physicianSection = renderQuestionSection(
        "Physician",
        QuestionCategories.physician,
        questions,
    );

    const contractSection = renderQuestionSection(
        "Contract Details",
        QuestionCategories.contract,
        questions,
    );

    const staffSection = renderQuestionSection(
        "Desired Staffing",
        QuestionCategories.staff,
        questions,
    );

    const flexibleSection = renderQuestionSection(
        "Flexible",
        QuestionCategories.flexible,
        questions,
    );

    const amenitySection = renderQuestionSection(
        "Amenities",
        QuestionCategories.amenity,
        questions,
    );

    const otherDetails = renderQuestionSection(
        "Other Details",
        QuestionCategories.otherDetails,
        questions,
    );

    const sections = [
        communityInfoSection,
        contactSection,
        communityRecordsSection,
        contractSection,
        communityAttributesSection,
        financesSection,
        activitySection,
        otherDetails,
        staffSection,
        healthSection,
        amenitySection,
        servicesSection,
        insuranceSection,
        physicianSection,
        flexibleSection,
    ].filter(section => section.fields.length > 0);

    return <PrintableForm sections={sections} />;
};

function buildPricingRows(
    pricingQuestions: IQuestion[],
    questions: IQuestion[],
    communityTypes: IPrompt[],
) {
    const pricingRows: PricingRow[] = [];

    for (const question of pricingQuestions) {
        const rule = question.rules?.asJson;

        if (filterRoomTypeQuestions(rule?.question)) {
            const dependentQuestion = questions?.find(q => q.key === rule.question);

            if (dependentQuestion) {
                const prompts = dependentQuestion.prompts || [];

                const communityType = communityTypes.find(
                    type => type.key === dependentQuestion.rules?.asJson?.contains,
                );

                if (communityType) {
                    for (const prompt of prompts) {
                        if (
                            !pricingRows.find(
                                row =>
                                    row.floorPlan === prompt.title &&
                                    row.careType === communityType.title,
                            )
                        ) {
                            pricingRows.push({
                                careType: communityType?.title,
                                floorPlan: prompt.title,
                                monthlyPrice: undefined,
                            });
                        }
                    }
                }
            }
        }
    }

    for (const communityType of communityTypes) {
        if (!pricingRows.find(row => row.careType === communityType.title)) {
            pricingRows.push({
                careType: communityType.title,
                floorPlan: undefined,
                monthlyPrice: undefined,
            });
        }
    }

    // Create a map of communityType title -> order index
    const typeOrderMap = new Map<string, number>();
    communityTypes.forEach((type, index) => {
        typeOrderMap.set(type.title, index);
    });

    // Sort pricingRows by the careType order
    pricingRows.sort((a, b) => {
        const orderA = typeOrderMap.get(a.careType) ?? Infinity;
        const orderB = typeOrderMap.get(b.careType) ?? Infinity;
        return orderA - orderB;
    });

    return pricingRows;
}
